import React from 'react';
import { MenuProps } from 'antd';
import {
  UserOutlined,
  ShoppingOutlined,
  InboxOutlined,
  DeliveredProcedureOutlined,
  AccountBookOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { useEventContext } from '../../contexts/EventContext';
import { UserProfile } from '../../types';
import { useTranslation } from '../../hooks/useTranslation';

// Helper type: a single menu item from Ant Design MenuProps
type MenuItem = NonNullable<MenuProps['items']>[number];

interface ThirdPartyMenuProps {
  user: UserProfile;
  selectedKeys: string[];
  openKeys: string[];
  onOpenChange: (openKeys: string[]) => void;
  onMenuClick: (path: string) => void;
}

export const ThirdPartyMenu = ({
  user,
  selectedKeys: _selectedKeys,
  openKeys: _openKeys,
  onOpenChange: _onOpenChange,
  onMenuClick,
}: ThirdPartyMenuProps): { items: MenuItem[] } => {
  const { getOrgUrl } = useEventContext();
  const { t } = useTranslation();

  // Simple role-based access check
  const hasRoleAccess = (key: string): boolean => {
    if (!user) return false;

    // Platform users can access everything
    if (user.user_type === 'platform_user') return true;

    // For org users, check based on roles
    if (user.user_type === 'org_user') {
      const userRoles = user.roles || user.organization_roles || [];

      // org_admin can access all thirdparty menus
      if (userRoles.includes('org_admin')) return true;

      // Other roles with thirdparty access can see all items
      if (userRoles.includes('event_manager')) {
        return true; // event_manager has thirdparty access
      }
    }

    return false;
  };

  const createMenuItem = (key: string, label: string, icon: React.ReactNode, path: string): MenuItem | null => {
    if (!hasRoleAccess(key)) {
      return null;
    }

    const menuItem: MenuItem = {
      key,
      icon,
      label,
      onClick: () => onMenuClick(path),
    };

    return menuItem;
  };

  const thirdpartyMenuItems: MenuItem[] = [
    // TODO: Re-enable dashboard menu item
    // createMenuItem(
    //   'dashboard',
    //   t('menu.thirdparty.dashboard'),
    //   <BarChartOutlined />,
    //   getOrgUrl('thirdparty/dashboard')
    // ),
    createMenuItem(
      'suppliers',
      t('menu.thirdparty.suppliers'),
      <UserOutlined />,
      getOrgUrl('thirdparty/suppliers')
    ),
    createMenuItem(
      'products',
      t('menu.thirdparty.products'),
      <ShoppingOutlined />,
      getOrgUrl('thirdparty/products')
    ),
    // Inventory submenu with bulk import option
    hasRoleAccess('inventory') ? {
      key: 'inventory',
      icon: <InboxOutlined />,
      label: t('menu.thirdparty.inventory'),
      children: [
        {
          key: 'inventory-list',
          label: t('menu.thirdparty.viewInventory'),
          onClick: () => onMenuClick(getOrgUrl('thirdparty/inventory')),
        },
        {
          key: 'inventory-bulk-import',
          label: t('menu.thirdparty.bulkImportCodes'),
          onClick: () => onMenuClick(getOrgUrl('thirdparty/inventory/bulk-import')),
        },
      ],
    } : null,
    createMenuItem(
      'fulfillments',
      t('menu.thirdparty.fulfillments'),
      <DeliveredProcedureOutlined />,
      getOrgUrl('thirdparty/fulfillments')
    ),
    createMenuItem(
      'settlements',
      t('menu.thirdparty.settlements'),
      <AccountBookOutlined />,
      getOrgUrl('thirdparty/settlements')
    ),
    createMenuItem(
      'api-call-logs',
      t('menu.thirdparty.apiCallLogs'),
      <FileSearchOutlined />,
      getOrgUrl('thirdparty/api-call-logs')
    ),
  ].filter((item): item is MenuItem => item !== null);

  // Return a submenu group for 3rd party items
  return {
    items: [
      {
        type: 'divider',
      },
      {
        type: 'group',
        key: 'thirdparty-group',
        label: (
          <div
            style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {t('menu.thirdparty.items')}
          </div>
        ),
        children: thirdpartyMenuItems,
      },
    ],
  };
};
